import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getArtistProfile } from '../../api/commissions';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import '../../styles/nav.css';

interface NavLink {
  path: string;
  label: string;
}

const NAV_LINKS: NavLink[] = [
  { path: '/dashboard', label: 'Today' },
  { path: '/tarot', label: 'Tarot' },
  { path: '/calendar', label: 'Moon' },
  { path: '/oracle', label: 'Oracle' },
  { path: '/circle', label: 'Circle' },
  { path: '/profile', label: 'You' },
  { path: '/journey', label: 'Journey' },
  { path: '/essence', label: 'Soul Essence' },
  { path: '/reports', label: 'Blueprints' },
];

const AppNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSettingsActive = location.pathname === '/settings';
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const ADMIN_IDS = ['c4f8a468-7041-7050-0c66-55b2560872e2', '00ba8dc4-58fe-702b-76ba-2eb3e89fd6a8'];
  const [isArtist, setIsArtist] = useState(() => sessionStorage.getItem('zeal_isArtist') === 'true');
  const isAdmin = ADMIN_IDS.includes(user?.userId || '');

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Hide nav when keyboard is visible (iOS/Android)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listeners: any[] = [];

    const setupListeners = async () => {
      // Listen to all keyboard events for maximum reliability
      listeners.push(
        await Keyboard.addListener('keyboardWillShow', () => setKeyboardVisible(true)),
        await Keyboard.addListener('keyboardDidShow', () => setKeyboardVisible(true)),
        await Keyboard.addListener('keyboardWillHide', () => setKeyboardVisible(false)),
        await Keyboard.addListener('keyboardDidHide', () => setKeyboardVisible(false))
      );
    };

    setupListeners();

    return () => {
      listeners.forEach(listener => listener?.remove());
    };
  }, []);

  // Check artist status (cached in sessionStorage to avoid layout twitch)
  useEffect(() => {
    if (!user?.userId) return;
    if (sessionStorage.getItem('zeal_isArtist_checked') === user.userId) return;
    getArtistProfile(user.userId)
      .then(profile => {
        const approved = !!(profile && profile.status === 'approved');
        setIsArtist(approved);
        sessionStorage.setItem('zeal_isArtist', String(approved));
        sessionStorage.setItem('zeal_isArtist_checked', user.userId);
      })
      .catch(() => {});
  }, [user]);

  const navBtn = (path: string, label: string, isActive: boolean, isGear = false) => (
    <button
      key={path}
      onClick={() => { navigate(path); setMenuOpen(false); }}
      style={{
        padding: isGear ? '10px 18px' : '10px 18px',
        fontSize: isGear ? '16px' : '15px',
        color: isActive ? '#c9a84c' : 'rgba(255,255,255,0.5)',
        background: isActive ? 'rgba(201,168,76,0.1)' : 'transparent',
        border: isActive ? '1px solid rgba(201,168,76,0.2)' : '1px solid transparent',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: isActive ? 600 : 400,
        letterSpacing: '0.5px',
        transition: 'all 0.2s',
        lineHeight: isGear ? 1 : undefined,
        width: '100%',
        textAlign: 'center',
      }}
      onMouseEnter={e => {
        if (!isActive) {
          e.currentTarget.style.color = '#fff';
          e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
        }
      }}
      onMouseLeave={e => {
        if (!isActive) {
          e.currentTarget.style.color = isGear ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.5)';
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      {label}
    </button>
  );

  // Hide nav completely when keyboard is visible
  if (keyboardVisible) {
    return null;
  }

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        paddingBottom: '10px',
        paddingLeft: '24px',
        paddingRight: '20px',
        background: 'linear-gradient(180deg, rgba(10,10,15,0.95) 0%, rgba(10,10,15,0.85) 100%)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: 'none',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}>
        {/* Ethereal glow border */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.4) 30%, rgba(201,168,76,0.6) 50%, rgba(201,168,76,0.4) 70%, transparent)',
          animation: 'nav-glow-pulse 3s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-1px',
          left: '20%',
          right: '20%',
          height: '4px',
          background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.15) 0%, transparent 70%)',
          filter: 'blur(3px)',
          animation: 'nav-glow-pulse 3s ease-in-out infinite',
        }} />

        <span
          onClick={() => navigate('/dashboard')}
          className="zeal-logo-glow"
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: '14px',
            letterSpacing: '3px',
            color: '#c9a84c',
            cursor: 'pointer',
            textShadow: '0 0 12px rgba(201,168,76,0.3), 0 0 30px rgba(201,168,76,0.1)',
            transition: 'text-shadow 0.3s ease',
          }}
        >
          ZEAL GENESIS™
        </span>

        {/* Menu button (always visible, desktop + mobile) */}
        <button
          className="nav-hamburger nav-blackhole-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            position: 'relative',
            width: '38px',
            height: '38px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className={`nav-blackhole ${menuOpen ? 'active' : ''}`}>
            <div className="blackhole-ring blackhole-ring-1" />
            <div className="blackhole-ring blackhole-ring-2" />
            <div className="blackhole-ring blackhole-ring-3" />
            <div className="blackhole-core" />
          </div>
        </button>
      </nav>

      {/* Dropdown menu (all devices) */}
      <div className={`nav-mobile-menu ${menuOpen ? 'open' : ''}`}>
        {NAV_LINKS.map(link => navBtn(link.path, link.label, location.pathname === link.path))}
        {isArtist && navBtn('/artist', '🎨 Artist', location.pathname === '/artist')}
        {navBtn('/settings', 'Settings', isSettingsActive)}
      </div>
    </>
  );
};

export default AppNav;
